import * as profileApi from 'Api/profile';
import { httpClientInvestment } from 'Api/microservice-http-clients';
import { PROFILE_TYPES } from 'Const/investment';
import { authWSClient } from 'WSClient/index';
import { isObject } from 'Utils/common';
import merge from 'Utils/lodash.merge';
import {
  ADD_TRANSACTION_DATA, DELETE_INVESTMENT_PROFILE,
  LESS_TRANSACTION_DATA,
  SET_ALL,
  SET_PROFILE_SSN,
  SET_TRANSACTION_STATUS_CANCELLED,
  UPDATE_INVESTMENT_PROFILE_DATA,
} from './mutation-types';
import initialState from './state';

let investmentProfileListLoadingPromise = null;
let beneficialOwnersLoadingPromise = null;
export default {
  async resetProfile({ commit }) {
    const initialStateData = initialState();
    // Profile model should be form model always
    const { default: BaseFormModel } = await import(/* webpackChunkName: 'BaseFormModel' */ 'Models/BaseFormModel');
    initialStateData.profileModel = new BaseFormModel();
    commit(SET_ALL, initialStateData);
  },
  async getProfileData({ commit, getters, state, dispatch }, { updateEmptyFieldsData } = {}) {
    if (getters.profileDataLoaded) return { data: getters.profileData };
    if (getters.profileDataLoading) return getters.profileDataPromise;
    const { default: ProfileModel } = await import(/* webpackChunkName: 'ProfileModel' */ 'Models/ProfileModel');
    const profileModel = new ProfileModel();
    commit(SET_ALL, { profileModel });
    // Trick for router dependency manager
    // It's require to do not load ProfileModel in main bundle
    // Otherwise profile model will load even user is not authorized
    const response = await profileModel.$dataLoadingPromise;
    state.profileDataPromiseTempResolve(response);
    if (updateEmptyFieldsData) {
      await dispatch('updateProfileData', {
        data: updateEmptyFieldsData,
        emptyFieldsOnly: true,
      });
    }
    return response;
  },
  getNewProfileData({ state }) {
    return state.profileModel._getData();
  },
  async updateMetaProfileData({ getters, dispatch }, { data }) {
    const { profileModel } = getters;
    const profileModelMeta = profileModel.metaData;
    merge(profileModelMeta, data);
    profileModel.meta = JSON.stringify(profileModelMeta);
    await dispatch('updateProfileData', {
      data: profileModel,
    });
  },
  updateProfileData({ getters }, { data, emptyFieldsOnly }) {
    const { profileModel } = getters;
    profileModel.$setAllDefinedFields(data, emptyFieldsOnly);
    // Do nothing if there is no changes
    if (!profileModel.$hasChangedData) return Promise.resolve();
    // Do not show success message
    profileModel.$skipShowMessageOnce = true;
    return profileModel.send();
  },
  updateProfileDataWithNoSend({ state }, data) {
    state.profileModel.$updateData(data);
  },
  setupNotification({ dispatch }) {
    authWSClient.subscribe(
      'verification',
      null,
      message => dispatch('handleNotification', message),
    );
    authWSClient.subscribe(
      'refresh',
      null,
      message => dispatch('handleNotification', message),
    );
  },
  handleNotification({ dispatch }, message) {
    switch (message.channel) {
      case 'verification': {
        if (isObject(message.message)) {
          if (message.message.profile_id) {
            dispatch('updateInvestmentProfileDataFromWS', message.message);
          } else {
            dispatch('updateProfileDataWithNoSend', message.message);
          }
        }
        break;
      }
      case 'refresh': {
        dispatch('refreshData', message.message);
        break;
      }
      default: break;
    }
  },
  getInvestmentProfileList({ commit, state }, { force } = {}) {
    if (investmentProfileListLoadingPromise) return investmentProfileListLoadingPromise;
    // do not reload if it loaded and not required to reload
    if (state.investmentProfileListLoaded && !force) return Promise.resolve(state.investmentProfileList);
    commit(SET_ALL, { investmentProfileListLoading: true });
    investmentProfileListLoadingPromise = httpClientInvestment.get('/profile')
      .then(({ data }) => {
        commit(SET_ALL, {
          investmentProfileList: data.data,
          investmentProfileListLoading: false,
          investmentProfileListLoaded: true,
        });
        investmentProfileListLoadingPromise = null;
        return data.data;
      })
      .catch((e) => {
        investmentProfileListLoadingPromise = null;
        commit(SET_ALL, { investmentProfileListLoading: false });
        throw e;
      });
    return investmentProfileListLoadingPromise;
  },
  setSelectedInvestmentProfileId({ commit }, profileId) {
    commit(SET_ALL, { selectedInvestmentProfileId: profileId });
  },
  setDefaultInvestmentProfile({ dispatch, getters }) {
    dispatch(
      'setSelectedInvestmentProfileId',
      getters.lastUsedInvestmentProfileId || getters.individualInvestmentProfileId,
    );
  },
  setSelectedInvestmentProfileIndividual({ getters, dispatch }) {
    dispatch('setSelectedInvestmentProfileId', getters.individualInvestmentProfileId || null);
  },
  async getInvestmentProfileData({ commit }, profileId) {
    if (!profileId) return;
    const { data } = await httpClientInvestment.get(`/profile/${profileId}`);
    commit(UPDATE_INVESTMENT_PROFILE_DATA, {
      profileId,
      data: {
        ...data,
        fullLoaded: true,
        ssn_missing: data.ssn_missing || false,
        ein_missing: data.ein_missing || false,
      },
    });
  },
  async getSelectedInvestmentProfileData({ getters, dispatch }) {
    if (!getters.selectedInvestmentProfileId) return;
    await dispatch('getInvestmentProfileData', getters.selectedInvestmentProfileId);
  },
  updateSelectedInvestmentProfileData({ commit, getters }, data) {
    commit(UPDATE_INVESTMENT_PROFILE_DATA, {
      profileId: getters.selectedInvestmentProfileId,
      data,
    });
  },
  updateInvestmentProfileDataFromWS({ commit }, { profile_id: profileId, ...data } = {}) {
    commit(UPDATE_INVESTMENT_PROFILE_DATA, { profileId, data });
  },
  async deleteSelectedInvestmentProfile({ getters, commit, dispatch }) {
    await httpClientInvestment.delete(`/profile/${getters.selectedInvestmentProfileId}`);
    commit(DELETE_INVESTMENT_PROFILE, getters.selectedInvestmentProfileId);
    dispatch('setDefaultInvestmentProfile');
  },
  resetWalletData({ commit }) {
    commit(SET_ALL, {
      walletData: {},
      walletDataProfileId: null,
      walletBalance: {},
      walletBanks: [],
      walletTransactionsData: [],
      walletTransactionsCount: 0,
      walletCanLinkBankAccount: true,
    });
  },
  getWalletData({ commit, state, getters, dispatch }, forceReload) {
    if (
      // need ready KYC
      !getters.isIdVerificationSent
      // no wallet for SDIRA accounts
      || (getters.selectedInvestmentProfile
      && getters.selectedInvestmentProfile.type === PROFILE_TYPES.SDIRA)
    ) {
      // Reset wallet data
      dispatch('resetWalletData');
      return Promise.resolve();
    }
    // do not trigger loading twice at once for same investment profile
    if (state.walletDataLoadingPromise && state.walletDataLoadingProfileId === getters.selectedInvestmentProfileId) {
      return state.walletDataLoadingPromise;
    }
    // need force reload option to load wallet for already loaded profile id
    if (!forceReload && state.walletDataProfileId === getters.selectedInvestmentProfileId) {
      return Promise.resolve(state.walletData);
    }
    state.walletDataLoadingProfileId = getters.selectedInvestmentProfileId;
    commit(SET_ALL, { walletDataLoading: true });
    state.walletDataLoadingPromise = profileApi.getWallet(getters.selectedInvestmentProfileId)
      .then(({ data }) => {
        commit(SET_ALL, {
          walletData: data,
          walletDataProfileId: getters.selectedInvestmentProfileId,
          walletBalance: data.balance,
          walletBanks: data.banks,
          walletCanLinkBankAccount: data.is_possibly,
          walletCanWithdraw: data.is_withdraw_possible,
          walletDataLoading: false,
          walletDataLoaded: true,
          walletTransactionsData: data.balance.transactions || [],
          walletTransactionsCount: data.balance.total || 0,
        });
        return data;
      })
      .finally(() => {
        state.walletDataLoadingProfileId = null;
        commit(SET_ALL, { walletDataLoading: false });
        state.walletDataLoadingPromise = null;
      });
    return state.walletDataLoadingPromise;
  },
  async removeWalletBankAccount({ commit, getters }, accountData) {
    commit(SET_ALL, { walletDataLoading: true });
    try {
      const { data } = await profileApi.removeWalletBankAccount(accountData, getters.selectedInvestmentProfileId);
      commit(SET_ALL, { walletDataLoading: false });
      return data;
    } catch (error) {
      commit(SET_ALL, { walletDataLoading: false });
      throw error;
    }
  },
  addFundingSource({ commit, getters }, source) {
    commit(SET_ALL, { addFundingSourceLoading: true });
    return profileApi.addFundingSource(source, getters.selectedInvestmentProfileId)
      .catch((e) => {
        commit(SET_ALL, { addFundingSourceLoading: false });
        throw e;
      });
  },
  getWalletTransactions({ state, commit, getters }) {
    // Complete KYC first or do not request twice in the same time
    if (!getters.isIdVerificationComplete || state.walletTransactionsLoading) return Promise.resolve();
    commit(SET_ALL, { walletTransactionsLoading: true });
    return profileApi.getWalletTransactions(
      '10',
      getters.walletTransactionsLoadedCount,
      getters.selectedInvestmentProfileId,
    )
      .then(({ data }) => {
        commit(ADD_TRANSACTION_DATA, data.data);
        commit(SET_ALL, {
          walletTransactionsCount: data.total,
          walletTransactionsLoading: false,
          walletTransactionsLoaded: true,
        });
        return data;
      })
      .catch((error) => {
        commit(SET_ALL, { walletTransactionsLoading: false });
        return error;
      });
  },
  lessWalletTransactions({ commit }) {
    commit(LESS_TRANSACTION_DATA);
  },
  resetWalletTransactions({ commit }) {
    commit(SET_ALL, {
      walletTransactionsData: [],
      walletTransactionsLoaded: false,
    });
  },
  cancelDwollaTransaction({ commit, getters }, id) {
    commit(SET_ALL, { walletTransactionsLoading: true });
    return profileApi.cancelDwollaTransaction(id, getters.selectedInvestmentProfileId)
      .then(({ data }) => {
        commit(SET_TRANSACTION_STATUS_CANCELLED, id);
        commit(SET_ALL, {
          walletTransactionsLoading: false,
        });
        return data;
      })
      .catch((error) => {
        commit(SET_ALL, { walletTransactionsLoading: false });
        throw error;
      });
  },
  getBeneficialOwners({ commit, getters, dispatch, state }, force) {
    // If profile doen't not required BO, reset data and return
    if (!getters.isBeneficialOwnersRequired) {
      dispatch('resetBeneficialOwners');
      return Promise.resolve();
    }
    // if needed BOs loading right now, return exist promise
    if (getters.selectedInvestmentProfileId === state.beneficialOwnersLoadingProfileId) {
      return beneficialOwnersLoadingPromise;
    }
    // if needed profile BOs data is already loaded and not needed to reload, return data
    if (getters.selectedInvestmentProfileId === state.beneficialOwnersDataProfileId && !force) {
      return Promise.resolve(getters.beneficialOwnersData);
    }
    // If needed to load profile is different than needed BO data, reset bo data before get
    if (getters.beneficialOwnersLoaded
      && getters.selectedInvestmentProfileId !== state.beneficialOwnersDataProfileId
    ) {
      dispatch('resetBeneficialOwners');
    }
    commit(SET_ALL, {
      beneficialOwnersLoading: true,
      beneficialOwnersLoadingProfileId: getters.selectedInvestmentProfileId,
    });
    beneficialOwnersLoadingPromise = profileApi.getBeneficialOwners(getters.selectedInvestmentProfileId)
      .then(({ data }) => {
        commit(SET_ALL, {
          beneficialOwnersData: data,
          beneficialOwnersLoading: false,
          beneficialOwnersLoaded: true,
          beneficialOwnersDataProfileId: getters.selectedInvestmentProfileId,
          beneficialOwnersLoadingProfileId: null,
        });
        return data;
      })
      .catch((error) => {
        commit(SET_ALL, {
          beneficialOwnersLoading: false,
          beneficialOwnersLoadingProfileId: null,
        });
        return error;
      });
    return beneficialOwnersLoadingPromise;
  },
  resetBeneficialOwners({ commit }) {
    commit(SET_ALL, {
      beneficialOwnersData: [],
      beneficialOwnersLoaded: false,
      beneficialOwnersDataProfileId: null,
    });
  },
  refreshData({ dispatch }, list = []) {
    list.forEach((item) => {
      if (item === 'wallet') {
        dispatch('getWalletData', true);
      } else if (item === 'investment') {
        dispatch('investment/getInvestmentList', true, { root: true });
        dispatch('investment/getInvestmentCompletedSuccessfulList', true, { root: true });
      } else if (item === 'accreditation-issues') {
        dispatch('getInvestmentProfileAccreditationIssueList', { force: true });
      } else if (item.startsWith('profile:')) {
        try {
          const profileId = item.split(':')[1];
          dispatch('getInvestmentProfileData', profileId);
        } catch (e) {
          // ignore errors here, because update should be silent
        }
      }
    });
  },
  setInvestmentProfileSSN({ commit }, { profileId, ssn }) {
    if (!ssn) return;
    // Remove dashes in ssn before save
    commit(SET_PROFILE_SSN, { profileId, ssn: ssn.replace(/-/g, '') });
  },
  setSelectedInvestmentProfileSSN({ dispatch, getters }, ssn) {
    dispatch('setInvestmentProfileSSN', {
      profileId: getters.selectedInvestmentProfileId,
      ssn,
    });
  },
  async getInvestmentProfileAccreditationIssueList({ commit, state }, { force } = {}) {
    if (state.investmentProfileAccreditationIssueListLoaded && !force) {
      return Promise.resolve(state.investmentProfileAccreditationIssueList);
    }
    const { data } = await httpClientInvestment.get('/accreditation-issues');
    commit(SET_ALL, {
      investmentProfileAccreditationIssueList: data || [],
      investmentProfileAccreditationIssueListLoaded: true,
    });
    return data;
  },
};
